import classNames from 'classnames';
import {useEffect, useRef, useState} from 'react';
import {useSelector} from 'react-redux';
import {useLocation, useNavigate} from 'react-router-dom';
import DotsLoading from '../../../components/dotsLoading';
import SecondaryCard from '../../../components/mazraeKit/cards/secondaryCard';
import CustomPaginate from '../../../components/mazraeKit/customPaginate';
import CustomSearchBox from '../../../components/mazraeKit/customSearchBox';
import MainFooter from '../../../components/mazraeKit/footers/footerMain';
import CustomHeader from '../../../components/mazraeKit/headers/homeHeader';
import NoItemFoundPage from '../../../components/noItemFoundPage';
import {useGetNearestStores} from '../../../services/hookService/getNearestStores/useGetNearestStores';
import UseGetProfileCity from '../../../services/hookService/getProfileCityName/useGetProfileCity';
import {RootState} from '../../../store/redux';
import {googleAnalyticsInstance} from '../../../utilities/helper/googleAnalitics';
import useDebounce from '../../../utilities/hooks/useDebounce/useDebounce';
import useScrolling from '../../../utilities/hooks/useScrolling/useScrolling';
import ShopListingBottomSheet from '../shopListingBottomSheet';
import FiltersShopList from './filtersShopList';
import {useShopListingFilters} from '../../../hooks/filters/useShopListingFilter';

const PER_PAGE_SIZE = 15; // backend default page size

const ShopListing = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const scrollDivElement = useRef<HTMLDivElement | null>(null);
  const {isScrollingDown} = useScrolling(scrollDivElement);
  const {setShopListingFilter, shopListingFilter} = useShopListingFilters();

  const [inputValue, setInputValue] = useState<string>(
    shopListingFilter.search ?? '',
  );
  const [openBottomSheet, setOpenBottomSheet] = useState(false);
  const [currentPage, setcurrentPage] = useState(1);

  const debouncedValue = useDebounce(inputValue, 2000);

  useEffect(() => {
    setShopListingFilter('search', debouncedValue);
  }, [debouncedValue]);

  const onChangeHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
  };

  const {locational} = useSelector(
    (state: RootState) => state.searchingLocation,
  );
  const cities = locational.map(item => item.id).join(',');

  const {nearestStores, nearestStoresIsLoading} = useGetNearestStores({
    search: shopListingFilter.search || undefined,
    input_groups_guid:
      shopListingFilter.input_guid?.map(item => item.guid).join(',') ||
      undefined,
    ordering: shopListingFilter.order_by || undefined,
    in_person_possibility: shopListingFilter.in_person_possibility || undefined,
    has_delivery: shopListingFilter.has_delivery || undefined,
    has_guarantee: shopListingFilter.has_guarantee || undefined,
    has_discount: shopListingFilter.has_discount || undefined,
    selected_cities_id: cities || undefined,
    app: 'supplier',
    page: currentPage,
  });

  return (
    <div className="flex relative flex-col w-full h-dvh">
      <div>
        <CustomHeader
          canScroll={true}
          isScrolling={false}
          title="فروشگاه ها"
          preIcon="icon-nameArrow---Right"
          isActiveRightIcon
          onClick={() => {
            navigate('/home');
          }}
        />
      </div>
      <div
        className={classNames(
          'overflow-y-hidden px-3 my-3 w-full duration-500 ease-in-out',
          {
            'h-0 opacity-0': isScrollingDown,
            'h-52 opacity-100': !isScrollingDown,
          },
        )}
      >
        <div className="mt-3 mb-6 w-full">
          <CustomSearchBox
            placeholder="جستجو در فروشگاه "
            type="text"
            value={inputValue}
            inputCustomClass="!pr-2 !placeholder:text-md !text-md text-gray-700"
            isFloating={false}
            preIcon="icon-nameSearch"
            onChange={onChangeHandler}
            removeIconClassName="!left-20"
            customComponent={() => (
              <div
                className="flex absolute left-0 top-1/2 items-center px-2 py-2 border-r-2 border-gray-100 -translate-y-1/2"
                onClick={() =>
                  navigate('/choose-location', {
                    state: {from: location?.pathname},
                  })
                }
              >
                <i className="text-xl icon-nameLocation" />
                <span
                  className="text-sm"
                  onClick={() =>
                    navigate('/choose-location', {
                      state: {from: location?.pathname},
                    })
                  }
                >
                  {locational?.length
                    ? `${locational?.length} شهرستان`
                    : UseGetProfileCity().profileCity}
                </span>
              </div>
            )}
            {...{inputValue, setInputValue}}
          />
          <div className="flex overflow-x-auto gap-2 items-center mt-3 w-full">
            <button
              className="flex gap-2 items-center px-3 py-2 text-gray-700 rounded-full border w-fit"
              onClick={() => {
                googleAnalyticsInstance.eventGoogleAnalytics({
                  category: 'shop-filter',
                  action: 'رو فیلتر کردن فروشگاه ها کلیک شد',
                });
                setOpenBottomSheet(true);
              }}
            >
              <i className="font-bold icon-nameFilter text-xxl" />
              <span className="text-sm whitespace-nowrap">فیلتر کردن</span>
            </button>
            <div>
              <FiltersShopList />
            </div>
          </div>
        </div>
      </div>
      <section
        className="flex overflow-y-auto flex-col gap-4 px-3 h-full"
        ref={scrollDivElement}
      >
        {nearestStoresIsLoading && <DotsLoading />}
        {!nearestStoresIsLoading && !!!nearestStores?.data?.results?.length && (
          <NoItemFoundPage />
        )}
        {nearestStores?.data?.results.map((item: IResults, index: number) => (
          <SecondaryCard
            rate={item.average_rate}
            key={item.guid}
            logo={item.logo}
            title={item.title}
            state={item.state?.name || ''}
            city={item.city?.name || ''}
            cardWrapperClassName="w-full"
            input_groups={item?.store_type?.input_groups}
            guid={item.guid}
            onClick={() => navigate(`/shop-card-detail/${item.guid}`)}
          />
        ))}
      </section>
      <div
        className={classNames('duration-500 ease-in-out', {
          'overflow-y-hidden my-2 h-0 opacity-0': isScrollingDown,
          'my-3 opacity-100': !isScrollingDown,
        })}
      >
        {nearestStores?.data && (
          <CustomPaginate
            currentPage={currentPage}
            onPageChange={setcurrentPage}
            totalCount={nearestStores?.data?.count}
            perPageSize={PER_PAGE_SIZE}
          />
        )}
      </div>

      <ShopListingBottomSheet {...{openBottomSheet, setOpenBottomSheet}} />
      <MainFooter isScrolling={isScrollingDown} />
    </div>
  );
};

export default ShopListing;
